import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { useInView } from 'react-intersection-observer';

import {
  FeedListInfinitySpinner,
  EmptyFeed,
  FeedListItem,
  LoaderDashboard,
} from 'components/shared';
import { getProfileOwnsFeedQuery } from 'services/apollo/queries/profileQueries';
import { useTranslation } from 'hooks';
import { handleFetchMoreError, criticalFeedError } from 'services';
import { useFeatureFlagState } from 'context';

import styles from './ProfileFeed.module.scss';

interface ProfileOwnedFeedProps {
  isCurrentUser?: boolean;
}

export const ProfileOwnedFeed: React.FC<ProfileOwnedFeedProps> = ({ isCurrentUser }) => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { groupsFeatureFlag } = useFeatureFlagState();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { error, data, fetchMore } = useQuery(getProfileOwnsFeedQuery, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    variables: { cursor: '', userId, includeGroups: groupsFeatureFlag },
  });

  const [ref, inView] = useInView({
    rootMargin: '500px 0px',
    threshold: 0,
  });

  /**
   * Loads more results into the activity feed
   */
  const triggerFetchMore = async () => {
    setIsLoadingMore(true);
    const { endCursor } = data.profileByIdentifier.ownsNodes.pageInfo;

    try {
      await fetchMore({
        variables: { cursor: endCursor },
        updateQuery: (prevResult: any, { fetchMoreResult }) => {
          fetchMoreResult.profileByIdentifier.ownsNodes.nodes = [
            ...prevResult.profileByIdentifier.ownsNodes.nodes,
            ...fetchMoreResult.profileByIdentifier.ownsNodes.nodes,
          ];

          setIsLoadingMore(false);
          return fetchMoreResult;
        },
      });
    } catch (e) {
      handleFetchMoreError(e);
    }
  };

  useEffect(() => {
    if (inView === true) {
      triggerFetchMore().then((response) => response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  if (data === undefined)
    return (
      <div className={styles.tetrisMain}>
        <LoaderDashboard layout="HeadlessFeed" />
      </div>
    );

  if (error && criticalFeedError(error))
    return (
      <div className={styles.tetrisMain}>
        <LoaderDashboard layout="HeadlessFeed" />
      </div>
    );

  const nodes = data?.profileByIdentifier?.ownsNodes?.nodes;
  const pageInfo = data?.profileByIdentifier?.ownsNodes?.pageInfo;

  if (nodes === undefined)
    return (
      <div className={styles.tetrisMain}>
        <LoaderDashboard layout="HeadlessFeed" />
      </div>
    );

  if (nodes?.length === 0)
    return (
      <div className={styles.tetrisMain}>
        <EmptyFeed
          message={
            isCurrentUser
              ? t('profile-screen.profile-feed.empty-feed-current-user')
              : t('profile-screen.profile-feed.empty-feed-created', {
                  name: data?.profileByIdentifier?.name,
                })
          }
        />
      </div>
    );

  return (
    <>
      {nodes?.map((node: any) => (
        <FeedListItem
          nodeId={node.id}
          nodeType={node.__typename}
          key={node.id}
          name={node.title}
          baseUrl={node.baseUrl}
          url={node.url}
          owner={node.owners[0]}
          collaborators={node.accessorInfo}
          modified={node.modified}
          created={node.created}
          nodeIcon={node.category}
          isIntegrationConnected={node.isIntegrationConnected}
          integrationType={node.integrationType}
          isShareable={node.sharingEnabled}
          shareTargets={node.shareTargets}
        />
      ))}

      {pageInfo?.hasNextPage && (
        <div ref={ref}>
          <FeedListInfinitySpinner isLoadingMore={isLoadingMore} />
        </div>
      )}
    </>
  );
};
